import { css } from "@emotion/react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Flex,
  Heading,
  Icon,
  Icons,
  Loading,
  RichTooltip,
  RichTooltipDescription,
  RichTooltipTitle,
  Tab,
  TabList,
  Tabs,
  Text,
  TooltipTrigger,
  View,
} from "@phoenix/components";
import {
  getChartColor,
  useSequentialChartColors,
} from "@phoenix/components/chart";
import { useNotifyError } from "@phoenix/contexts";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { authFetch } from "../../authFetch";
import { prependBasename } from "../../utils/routingUtils";

// ---------------------------------------------------------------------------
// Inline help tooltips
// ---------------------------------------------------------------------------

const helpButtonCSS = css`
  & {
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    margin-left: 6px;
    border-radius: var(--global-rounding-small);
    cursor: help;
    color: var(--global-text-color-500);
    vertical-align: middle;
  }

  & svg {
    width: 14px;
    height: 14px;
  }

  &:hover,
  &:focus-visible {
    color: var(--global-text-color-900);
    background: var(--global-color-gray-200);
  }
`;

/**
 * HelpTooltip
 *
 * Renders a small info icon that reveals a rich tooltip when hovered or
 * focused. Used to document every block/stat on the System Health page so an
 * operator can understand what a metric measures, where it comes from, and how
 * to read its thresholds without leaving the page.
 */
function HelpTooltip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <TooltipTrigger delay={200}>
      <Button
        variant="quiet"
        size="S"
        aria-label={`About ${label}`}
        css={helpButtonCSS}
        leadingVisual={<Icon svg={<Icons.InfoOutline />} />}
      />
      <RichTooltip width="360px">
        <RichTooltipTitle>{label}</RichTooltipTitle>
        {children}
      </RichTooltip>
    </TooltipTrigger>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AlarmState {
  state: "ok" | "warn" | "critical";
  since: string | null;
  last_value: number | null;
  // "host" for whole-machine metrics (evaluated once, not per worker),
  // "worker" for everything else. Absent on older stored snapshots.
  scope?: "worker" | "host";
}

interface WorkerLatest {
  cpu_percent?: number;
  memory_percent?: number;
  memory_mb?: number;
  asyncio_tasks?: number;
  queue_depth?: number;
  db_pool_saturation?: number;
  cgroup_memory_limit_bytes?: number;
  cgroup_memory_usage_bytes?: number;
  event_loop_lag_ms?: number;
  daily_budget_spent_pct?: number;
  queue_depth_pct?: number;
  capacity_limiter_saturation_pct?: number;
  rate_limiter_wait_s?: number;
  stuck_asyncio_task_s?: number;
  host_ram_available_pct?: number;
  host_swap_used_pct?: number;
  host_load_avg_vs_cores?: number;
  cgroup_cpu_quota_cores?: number;
  cgroup_memory_usage_pct?: number;
  host_ram_total_mb?: number;
  host_ram_available_mb?: number;
  host_swap_total_mb?: number;
  host_swap_used_mb?: number;
  host_load_avg_1m?: number;
  host_cpu_cores?: number;
  host_source?: string | null;
  ts: string;
}

interface WorkerHealth {
  name: string;
  instance_id: string;
  uptime_sec: number;
  samples_count: number;
  latest: WorkerLatest | null;
  last_error: string | null;
  last_error_at: string | null;
  alarm_state: Record<string, AlarmState> | null;
}

// Database reachability/size, probed server-side behind a 2s timeout + 10s
// cache. Never carries a connection string or credentials.
interface DatabaseHealth {
  reachable: boolean;
  dialect: string;
  latency_ms: number | null;
  connections: number | null;
  max_connections: number | null;
  connections_pct: number | null;
  size_bytes: number | null;
  idle_in_transaction: number | null;
  error: string | null;
  alarm_state: Record<string, AlarmState> | null;
  checked_at: string | null;
}

interface SystemHealthResponse {
  ts: string;
  workers_count: number;
  workers: WorkerHealth[];
  sample_interval_s: number;
  ring_size: number;
  // Whole-machine alarm state, surfaced once instead of repeated per worker.
  host_alarm_state: Record<string, AlarmState> | null;
  database?: DatabaseHealth | null;
}

interface ThresholdEntry {
  warn: number;
  critical: number;
}

interface ThresholdsResponse {
  thresholds: Record<string, ThresholdEntry>;
}

interface ActivitySummaryResponse {
  period_minutes: number;
  since: string;
  until: string;
  traces: { count: number };
  spans: { count: number };
  evaluations: {
    currently_running_count: number;
    running_target_count: number;
    running_project_count: number;
    currently_running: Array<{
      template_id: string;
      name: string;
      category: string;
      project_name: string | null;
      projects: string[];
      project_count: number;
      started_at: string | null;
      last_check_at: string | null;
      processed_traces: number;
    }>;
    started_in_period_count: number;
    started_in_period: Array<{
      template_id: string;
      name: string;
      category: string;
      project_name: string | null;
      projects: string[];
      project_count: number;
      started_at: string | null;
      last_check_at: string | null;
      processed_traces: number;
    }>;
  };
  red_teaming: {
    started_in_period_count: number;
    started_in_period: Array<{
      id: string;
      scan_type: string;
      target: string;
      created_at: string;
      has_results: boolean;
    }>;
    note: string;
  };
}

interface HistorySample {
  ts: string;
  cpu_percent?: number;
  memory_mb?: number;
  memory_percent?: number;
  asyncio_tasks?: number;
  queue_depth?: number;
  cgroup_cpu_usec?: number | null;
  cgroup_memory_limit_bytes?: number | null;
  cgroup_memory_usage_bytes?: number | null;
  db_pool_saturation?: number | null;
  pool_saturated?: boolean;
  event_loop_lag_ms?: number | null;
  queue_depth_pct?: number | null;
  capacity_limiter_saturation_pct?: number | null;
  rate_limiter_wait_s?: number | null;
  daily_budget_spent_pct?: number | null;
  stuck_asyncio_task_s?: number | null;
  host_ram_available_pct?: number | null;
  host_swap_used_pct?: number | null;
  host_load_avg_vs_cores?: number | null;
  cgroup_cpu_quota_cores?: number | null;
  cgroup_memory_usage_pct?: number | null;
  host_ram_total_mb?: number | null;
  host_ram_available_mb?: number | null;
  host_swap_total_mb?: number | null;
  host_swap_used_mb?: number | null;
  host_load_avg_1m?: number | null;
  host_cpu_cores?: number | null;
  host_source?: string | null;
}

interface HistoryWorker {
  name: string;
  instance_id: string;
  samples: HistorySample[];
}

interface HealthHistoryResponse {
  ts: string;
  workers: HistoryWorker[];
}

// Item 48 — Activity Feed
interface ActivityEvent {
  ts: string;
  actor: string;
  type: string;
  outcome: string;
  detail: Record<string, unknown>;
  trace_id: string | null;
  seq: number;
}

interface ActivityFeedResponse {
  ts: string;
  count: number;
  events: ActivityEvent[];
}

// Item 50 — Trend Analysis
interface TrendAnalysisEntry {
  current: number | null;
  baseline: number | null;
  delta_pct: number | null;
  anomalous: boolean;
  severity: string | null;
  samples: number;
  window: string;
}

interface TrendsResponse {
  ts: string;
  kpis: Record<string, TrendAnalysisEntry>;
}

// Item 49 — Per-template/project golden-signals
interface TemplateProjectRow {
  template_id: string;
  project_name: string;
  enabled: boolean;
  judge_calls: number;
  judge_errors: number;
  error_rate_pct: number;
  avg_latency_ms: number;
  annotations_written: number;
  budget_skips: number;
}

interface TemplateProjectTableResponse {
  ts: string;
  count: number;
  enabled_count: number;
  inactive_count: number;
  rows: TemplateProjectRow[];
}

// Item 51 — Freshness / SLO
interface FreshnessRow {
  template_id: string;
  project_name: string;
  freshness_pct: number;
  budget_remaining_pct: number;
  budget_exhausted: boolean;
  slo_target_pct: number;
  eligible_spans: number;
  fresh_spans: number;
  oldest_unjudged_age_s: number | null;
}

interface FreshnessSummary {
  overall_freshness_pct: number;
  budget_remaining_pct: number;
  budget_exhausted: boolean;
  slo_target_pct: number;
  total_eligible: number;
  total_fresh: number;
  target_s: number;
}

interface FreshnessResponse {
  ts: string;
  rows: FreshnessRow[];
  summary: FreshnessSummary;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function getAlarmSeverity(
  alarms: Record<string, AlarmState> | null
): "ok" | "warn" | "critical" {
  if (!alarms) return "ok";
  let severity: "ok" | "warn" | "critical" = "ok";
  for (const alarm of Object.values(alarms)) {
    if (alarm.state === "critical") return "critical";
    if (alarm.state === "warn") severity = "warn";
  }
  return severity;
}

// Severity of one metric's alarm on one worker. getAlarmSeverity() collapses a
// whole worker's alarm_state, which is wrong for a single-metric KPI card: a
// worker can be "critical" because of event-loop lag while its CPU is fine,
// and the CPU card must not inherit that.
function metricSeverity(
  worker: WorkerHealth | null | undefined,
  metric: string
): "ok" | "warn" | "critical" {
  const alarm = worker?.alarm_state?.[metric];
  return alarm ? getAlarmSeverity({ [metric]: alarm }) : "ok";
}

function workerBreakdown(
  workers: WorkerHealth[],
  pick: (w: WorkerHealth) => number | null | undefined,
  fmt: (v: number) => string
): string {
  return workers
    .map((w) => ({ name: w.name, value: pick(w) }))
    .filter((x): x is { name: string; value: number } => x.value != null)
    .sort((a, b) => b.value - a.value)
    .map((x) => `${x.name} ${fmt(x.value)}`)
    .join(" · ");
}

function collectAlarmsBySeverity(
  workers: WorkerHealth[],
  severity: "warn" | "critical"
): { metric: string; worker: string; value: number | null }[] {
  const results: { metric: string; worker: string; value: number | null }[] = [];
  for (const worker of workers) {
    if (!worker.alarm_state) continue;
    for (const [metric, alarm] of Object.entries(worker.alarm_state)) {
      if (alarm.state === severity) {
        results.push({ metric, worker: worker.name, value: alarm.last_value });
      }
    }
  }
  return results;
}

// Host alarms live at the top level of /v1/system/health, not inside any one
// worker, so they are collected separately and rendered once.
function collectHostAlarmsBySeverity(
  hostAlarmState: Record<string, AlarmState> | null | undefined,
  severity: "warn" | "critical"
): { metric: string; value: number | null }[] {
  if (!hostAlarmState) return [];
  const results: { metric: string; value: number | null }[] = [];
  for (const [metric, alarm] of Object.entries(hostAlarmState)) {
    if (alarm.state === severity) {
      results.push({ metric, value: alarm.last_value });
    }
  }
  return results;
}

function metricDisplayName(metric: string): string {
  const names: Record<string, string> = {
    process_cpu_pct: "CPU %",
    rss_pct: "Memory %",
    event_loop_lag_ms: "Event-loop lag",
    queue_depth_pct: "Queue depth %",
    capacity_limiter_saturation_pct: "Thread-pool saturation %",
    rate_limiter_wait_s: "Rate-limiter wait",
    daily_budget_spent_pct: "Daily budget spent %",
    db_pool_checked_out_pct: "DB pool checked-out %",
    stuck_asyncio_task_s: "Stuck asyncio task",
    host_ram_available_pct: "Host RAM available %",
    host_swap_used_pct: "Host swap used %",
    host_load_avg_vs_cores: "Host load vs cores",
    worker_cpu_pct: "Worker CPU % (avg)",
    worker_memory_pct: "Worker memory % (avg)",
    worker_queue_depth: "Worker queue depth",
    worker_asyncio_tasks: "Worker asyncio tasks",
    db_pool_saturation: "DB pool saturation (avg)",
    eval_judge_calls: "Judge calls / min",
    eval_avg_latency_ms: "Avg judge latency",
    eval_error_rate_pct: "Judge error rate",
  };
  return names[metric] || metric;
}

// Item 50 — long-form descriptions for the Trend Analysis panel. One entry per
// KPI the hourly roll-up records (see sampler._maybe_record_hourly_trends).
const trendMetricHelp: Record<string, string> = {
  worker_cpu_pct:
    "Average CPU usage across all workers, as a percentage of each container's CPU limit. Recorded once per hour.",
  worker_memory_pct:
    "Average resident memory across all workers, as a percentage of each container's memory limit. Recorded once per hour.",
  worker_queue_depth:
    "Total pending items across every worker's queue (online-eval deque, span queue, experiment queue) summed together. Recorded once per hour; a rising baseline means work is arriving faster than it is drained.",
  worker_asyncio_tasks:
    "Total live asyncio tasks in the process at sample time. A slowly climbing baseline usually means tasks are not being awaited or cleaned up.",
  db_pool_saturation:
    "Share of the SQLAlchemy async connection pool checked out, averaged across workers. A baseline creeping toward 1.0 precedes connection-wait stalls.",
  eval_judge_calls:
    "Judge calls per minute across all templates and projects, measured over the interval since the previous hourly sample. A spike here is the earliest signal of runaway eval volume — it is what rose before the 2026-09-10 incident.",
  eval_avg_latency_ms:
    "Mean judge-call latency over the interval since the previous hourly sample (interval latency total divided by interval judge calls, so per-template volume does not skew it). A rising baseline means the model providers or the scheduler are getting slower.",
  eval_error_rate_pct:
    "Average judge error rate across templates. A baseline step up means a provider, key, or template broke — check the Templates table for the specific pair.",
};

function trendMetricTooltip(kpi: string): ReactNode {
  const help = trendMetricHelp[kpi];
  if (!help) return null;
  return (
    <HelpTooltip label={metricDisplayName(kpi)}>
      <RichTooltipDescription>{help}</RichTooltipDescription>
      <RichTooltipDescription>
        The panel compares "now" against a baseline built from the trailing 7
        days at the same hour, and flags an anomaly when the current value is
        more than 3 standard deviations from that baseline.
      </RichTooltipDescription>
    </HelpTooltip>
  );
}

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------

type Severity = "ok" | "warn" | "critical";

const kpiCardCSS = (severity: Severity) => css`
  --kpi-accent: ${statusColor(severity)};
  position: relative;
  flex: 1 1 0;
  min-width: 168px;
  padding: 16px 18px;
  border-radius: var(--global-rounding-medium, 8px);
  border: 1px solid var(--global-border-color-default);
  background: var(--global-background-color-default);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    border-color 0.15s ease;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background: var(--kpi-accent);
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    border-color: color-mix(
      in srgb,
      var(--kpi-accent) 40%,
      var(--global-border-color-default)
    );
  }

  ${severity !== "ok" &&
  css`
    background: color-mix(
      in srgb,
      var(--kpi-accent) 6%,
      var(--global-background-color-default)
    );
  `}
`;

const statusDotCSS = (color: string) => css`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: ${color};
  box-shadow: 0 0 0 3px color-mix(in srgb, ${color} 18%, transparent);
  display: inline-block;
  flex: none;
`;

function statusColor(severity: Severity): string {
  if (severity === "critical") return "var(--global-color-danger, #ba1a1a)";
  if (severity === "warn") return "var(--global-color-warning, #ca8a04)";
  return "var(--global-color-success, #16a34a)";
}

const kpiLabelCSS = css`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--global-text-color-500);
`;

const kpiValueCSS = css`
  font-size: 1.75rem;
  line-height: 2rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--global-text-color-900);
`;

const kpiUnitCSS = css`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--global-text-color-500);
`;

// Per-worker breakdown shown under an aggregated KPI so the reader can see
// which process produced the headline number, not just the aggregate.
const kpiBreakdownCSS = css`
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.5;
  color: var(--global-text-color-500);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

function KPICard({
  title,
  value,
  unit,
  severity,
  help,
  breakdown,
}: {
  title: string;
  value: string;
  unit?: string;
  severity: Severity;
  help?: ReactNode;
  breakdown?: string;
}) {
  return (
    <div css={kpiCardCSS(severity)}>
      <Flex direction="column" gap="size-100">
        <Flex direction="row" alignItems="center" gap="size-100">
          <span css={statusDotCSS(statusColor(severity))} />
          <span css={kpiLabelCSS}>{title}</span>
          {help}
        </Flex>
        <Flex direction="row" alignItems="baseline" gap="size-50">
          <span css={kpiValueCSS}>{value}</span>
          {unit && <span css={kpiUnitCSS}>{unit}</span>}
        </Flex>
        {breakdown && (
          <span css={kpiBreakdownCSS} title={breakdown}>
            {breakdown}
          </span>
        )}
      </Flex>
    </div>
  );
}

const statusOverviewCSS = (severity: Severity) => css`
  --status-accent: ${statusColor(severity)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  border-radius: var(--global-rounding-medium, 8px);
  border: 1px solid
    color-mix(
      in srgb,
      var(--status-accent) 35%,
      var(--global-border-color-default)
    );
  background: color-mix(
    in srgb,
    var(--status-accent) 7%,
    var(--global-background-color-default)
  );
`;

function StatusOverview({
  workers,
  hostAlarmState,
  sampleIntervalS,
  updatedAt,
}: {
  workers: WorkerHealth[];
  hostAlarmState: Record<string, AlarmState> | null;
  sampleIntervalS: number;
  updatedAt: string | null;
}) {
  const critical =
    collectAlarmsBySeverity(workers, "critical").length +
    collectHostAlarmsBySeverity(hostAlarmState, "critical").length;
  const warn =
    collectAlarmsBySeverity(workers, "warn").length +
    collectHostAlarmsBySeverity(hostAlarmState, "warn").length;
  const severity: Severity =
    critical > 0 ? "critical" : warn > 0 ? "warn" : "ok";
  const title =
    critical > 0
      ? `${critical} critical alarm${critical === 1 ? "" : "s"} detected`
      : warn > 0
        ? `${warn} warning${warn === 1 ? "" : "s"} to review`
        : "All systems operational";
  const detail =
    critical > 0 || warn > 0
      ? "Review the highlighted metrics and the alarm banners below."
      : `No alarms across ${workers.length} worker${workers.length === 1 ? "" : "s"}.`;

  return (
    <div css={statusOverviewCSS(severity)}>
      <Flex direction="row" alignItems="center" gap="size-150">
        <span css={statusDotCSS(statusColor(severity))} />
        <Flex direction="column" gap="size-50">
          <Text weight="heavy">{title}</Text>
          <Text size="S" color="text-500">
            {detail}
          </Text>
        </Flex>
      </Flex>
      <Flex direction="row" alignItems="center" gap="size-100">
        <Badge variant="default">Sampled every {sampleIntervalS}s</Badge>
        {updatedAt && <Badge variant="default">Updated {updatedAt}</Badge>}
      </Flex>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Alarm Banners
// ---------------------------------------------------------------------------

function formatAlarmValue(value: number | null): string {
  if (value == null) return "";
  return typeof value === "number" && value < 10
    ? value.toFixed(2)
    : String(Math.round(value));
}

function AlarmBanners({
  workers,
  hostAlarmState,
}: {
  workers: WorkerHealth[];
  hostAlarmState: Record<string, AlarmState> | null;
}) {
  const criticalAlarms = collectAlarmsBySeverity(workers, "critical");
  const warnAlarms = collectAlarmsBySeverity(workers, "warn");
  const hostCritical = collectHostAlarmsBySeverity(hostAlarmState, "critical");
  const hostWarn = collectHostAlarmsBySeverity(hostAlarmState, "warn");

  const describeWorker = (a: { metric: string; worker: string; value: number | null }) =>
    `${metricDisplayName(a.metric)} ${a.value != null ? `(${formatAlarmValue(a.value)})` : ""} on ${a.worker}`;
  // A host condition is a property of the machine, not of one worker, so it is
  // reported once with a "(host)" suffix.
  const describeHost = (a: { metric: string; value: number | null }) =>
    `${metricDisplayName(a.metric)} ${a.value != null ? `(${formatAlarmValue(a.value)})` : ""} (host)`;

  const criticalText = [
    ...criticalAlarms.map(describeWorker),
    ...hostCritical.map(describeHost),
  ];
  const warnText = [
    ...warnAlarms.map(describeWorker),
    ...hostWarn.map(describeHost),
  ];

  return (
    <Flex direction="column" gap="size-100">
      {criticalText.length > 0 && (
        <Alert variant="danger" banner>
          <strong>CRITICAL</strong> — {criticalText.join("; ")}
        </Alert>
      )}
      {warnText.length > 0 && (
        <Alert variant="warning" banner>
          <strong>WARN</strong> — {warnText.join("; ")}
        </Alert>
      )}
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Workers Table
// ---------------------------------------------------------------------------

const tableCSS = css`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;

  th,
  td {
    padding: 10px 14px;
    text-align: left;
    vertical-align: middle;
    border-bottom: 1px solid var(--global-border-color-default);
  }

  th {
    font-weight: 600;
    color: var(--global-text-color-500);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    white-space: nowrap;
  }

  tbody tr {
    transition: background-color 0.12s ease;
  }

  tbody tr:nth-of-type(even) {
    background: rgba(var(--global-color-gray-500-rgb), 0.035);
  }

  tbody tr:hover {
    background: rgba(var(--global-color-gray-500-rgb), 0.08);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

function Th({ label, help }: { label: string; help?: ReactNode }) {
  return (
    <th>
      {label}
      {help}
    </th>
  );
}

function WorkersTable({ workers }: { workers: WorkerHealth[] }) {
  return (
    <table css={tableCSS}>
      <thead>
        <tr>
          <Th label="Name" />
          <Th
            label="Status"
            help={
              <HelpTooltip label="Status">
                <RichTooltipDescription>
                  Worst alarm state across every metric the worker reports:
                  Healthy (ok), warn, or critical. Derived from the latest
                  sample vs. the active thresholds.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
          <Th
            label="CPU"
            help={
              <HelpTooltip label="CPU">
                <RichTooltipDescription>
                  Latest CPU usage as a percentage of this container's CPU
                  limit.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
          <Th
            label="Memory"
            help={
              <HelpTooltip label="Memory">
                <RichTooltipDescription>
                  Latest resident memory as a percentage of this container's
                  memory limit.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
          <Th
            label="Event-loop lag"
            help={
              <HelpTooltip label="Event-loop lag">
                <RichTooltipDescription>
                  Latest event-loop scheduling lag for this worker in
                  milliseconds. Warning at 100 ms, critical at 500 ms.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
          <Th
            label="DB Pool"
            help={
              <HelpTooltip label="DB Pool">
                <RichTooltipDescription>
                  SQLAlchemy async connection-pool saturation: checked-out
                  connections as a percentage of the pool maximum.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
          <Th
            label="Samples"
            help={
              <HelpTooltip label="Samples">
                <RichTooltipDescription>
                  Number of samples this worker has contributed since it
                  started (one every 5 seconds).
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
          <Th
            label="Uptime"
            help={
              <HelpTooltip label="Uptime">
                <RichTooltipDescription>
                  Time since this worker instance started. A reset means the
                  process restarted.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
          <Th
            label="Last Error"
            help={
              <HelpTooltip label="Last Error">
                <RichTooltipDescription>
                  The most recent error recorded while sampling or ticking
                  this worker. Empty means no error has been observed.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
        </tr>
      </thead>
      <tbody>
        {workers.map((worker) => {
          const severity = getAlarmSeverity(worker.alarm_state);
          const latest = worker.latest;
          return (
            <tr key={worker.instance_id}>
              <td>
                <Text weight="heavy">{worker.name}</Text>
                <br />
                <Text size="S" color="text-500">
                  {worker.instance_id}
                </Text>
              </td>
              <td>
                <Badge
                  variant={
                    severity === "critical"
                      ? "danger"
                      : severity === "warn"
                        ? "warning"
                        : "success"
                  }
                >
                  {severity === "ok" ? "Healthy" : severity}
                </Badge>
              </td>
              <td>
                {latest?.cpu_percent != null
                  ? `${latest.cpu_percent.toFixed(1)}%`
                  : "—"}
              </td>
              <td>
                {latest?.memory_percent != null
                  ? `${latest.memory_percent.toFixed(1)}%`
                  : "—"}
              </td>
              <td>
                {latest?.event_loop_lag_ms != null
                  ? `${latest.event_loop_lag_ms.toFixed(0)}ms`
                  : "—"}
              </td>
              <td>
                {latest?.db_pool_saturation != null
                  ? `${(latest.db_pool_saturation * 100).toFixed(0)}%`
                  : "—"}
              </td>
              <td>{worker.samples_count}</td>
              <td>{formatDuration(worker.uptime_sec)}</td>
              <td>
                {worker.last_error ? (
                  <Text size="S" color="danger">
                    {worker.last_error}
                  </Text>
                ) : (
                  <Text size="S" color="text-500">
                    —
                  </Text>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Activity Summary
// ---------------------------------------------------------------------------

function ActivitySummary({
  activity,
}: {
  activity: ActivitySummaryResponse | null;
}) {
  if (!activity) {
    return <Loading />;
  }

  const runningEvals = activity.evaluations.currently_running;
  const runningProjects =
    activity.evaluations.running_project_count ??
    new Set(runningEvals.flatMap((e) => e.projects ?? [])).size;
  const scans = activity.red_teaming.started_in_period;

  return (
    <Flex direction="column" gap="size-200">
      <Flex direction="row" gap="size-200" wrap>
        <KPICard
          title="Traces (1h)"
          value={activity.traces.count.toLocaleString()}
          severity="ok"
          help={
            <HelpTooltip label="Traces (1h)">
              <RichTooltipDescription>
                Number of root spans (traces) ingested by this Phoenix instance
                in the last 60 minutes, across all projects. It is the raw
                incoming traffic that the online evaluators draw from — if it
                is zero, no eval will run regardless of worker health.
              </RichTooltipDescription>
            </HelpTooltip>
          }
        />
        <KPICard
          title="Spans (1h)"
          value={activity.spans.count.toLocaleString()}
          severity="ok"
          help={
            <HelpTooltip label="Spans (1h)">
              <RichTooltipDescription>
                Total spans (root plus children) ingested in the last 60
                minutes. Higher than the trace count by the average trace
                depth. Evaluators only judge spans that match a template's
                filter, so this is the pool of candidates they sample from.
              </RichTooltipDescription>
            </HelpTooltip>
          }
        />
        <KPICard
          title="Running Evals"
          value={activity.evaluations.currently_running_count.toString()}
          unit={
            runningProjects > 0
              ? `across ${runningProjects.toLocaleString()} project${
                  runningProjects === 1 ? "" : "s"
                }`
              : undefined
          }
          severity={
            activity.evaluations.currently_running_count > 0
              ? "ok"
              : "warn"
          }
          help={
            <HelpTooltip label="Running Evals">
              <RichTooltipDescription>
                Online evaluators that are currently opted in and running,
                counted once per evaluation (template). Each evaluation may be
                running against several projects at once; the project count
                beside the number is how many distinct projects are covered.
                Yellow at zero means no live evaluation is enabled.
              </RichTooltipDescription>
            </HelpTooltip>
          }
        />
        <KPICard
          title="Recent Scans"
          value={activity.red_teaming.started_in_period_count.toString()}
          severity="ok"
          help={
            <HelpTooltip label="Recent Scans">
              <RichTooltipDescription>
                Red-team scans started in the last 60 minutes. The table below
                lists each one with its type, target, creation time, and
                whether any results have been written yet.
              </RichTooltipDescription>
            </HelpTooltip>
          }
        />
      </Flex>

      {runningEvals.length > 0 && (
        <Card
          title="Running Evaluations"
          collapsible={false}
          titleExtra={
            <HelpTooltip label="Running Evaluations">
              <RichTooltipDescription>
                Live online evaluators currently opted in, one row per
                evaluation (template). When an evaluation runs against several
                projects they are listed together in the Project column.
              </RichTooltipDescription>
            </HelpTooltip>
          }
        >
          <table css={tableCSS}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Project</th>
                <th>Started</th>
                <th>Processed</th>
              </tr>
            </thead>
            <tbody>
              {runningEvals.map((e) => (
                <tr key={e.template_id}>
                  <td>
                    <Text weight="heavy">{e.name}</Text>
                  </td>
                  <td>{e.category}</td>
                  <td>{e.project_name ?? "—"}</td>
                  <td>
                    {e.started_at
                      ? new Date(e.started_at).toLocaleString()
                      : "—"}
                  </td>
                  <td>{e.processed_traces.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {scans.length > 0 && (
        <Card
          title="Red-Team Scans"
          collapsible={false}
          titleExtra={
            <HelpTooltip label="Red-Team Scans">
              <RichTooltipDescription>
                Red-team scans started in the period, one row each: scan id,
                attack type, target, creation time, and whether results have
                been written yet (Pending = still running or not yet
                persisted).
              </RichTooltipDescription>
            </HelpTooltip>
          }
        >
          <table css={tableCSS}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Target</th>
                <th>Created</th>
                <th>Results</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Text weight="heavy">{s.id}</Text>
                  </td>
                  <td>{s.scan_type}</td>
                  <td>{s.target}</td>
                  <td>
                    {s.created_at
                      ? new Date(s.created_at).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    <Badge
                      variant={s.has_results ? "success" : "warning"}
                    >
                      {s.has_results ? "Yes" : "Pending"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Text size="S" color="text-500">
        {activity.red_teaming.note}
      </Text>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// History Trends
// ---------------------------------------------------------------------------

const trendCardCSS = css`
  flex: 1 1 0;
  min-width: 280px;
  padding: 14px;
  border-radius: var(--global-rounding-medium, 8px);
  border: 1px solid var(--global-border-color-default);
  background: var(--global-background-color-default);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.07);
  }
`;

function transformHistoryForMetric(
  workers: HistoryWorker[],
  metric: keyof HistorySample
): Array<Record<string, number | string | null>> {
  const pointMap = new Map<string, Record<string, number | string | null>>();

  for (const worker of workers) {
    for (const sample of worker.samples) {
      const ts = sample.ts;
      if (!pointMap.has(ts)) {
        pointMap.set(ts, { ts });
      }
      const point = pointMap.get(ts)!;
      const value = sample[metric];
      point[worker.name] = typeof value === "number" ? value : null;
    }
  }

  return Array.from(pointMap.values()).sort(
    (a, b) =>
      new Date(a.ts as string).getTime() -
      new Date(b.ts as string).getTime()
  );
}

function hasAnyData(
  data: Array<Record<string, number | string | null>>,
  keys: string[]
): boolean {
  for (const pt of data) {
    for (const k of keys) {
      if (typeof pt[k] === "number") return true;
    }
  }
  return false;
}

function TrendChart({
  title,
  data,
  dataKeys,
  unit,
  help,
}: {
  title: string;
  data: Array<Record<string, number | string | null>>;
  dataKeys: string[];
  unit?: string;
  help?: ReactNode;
}) {
  const colors = useSequentialChartColors();
  return (
    <div>
      <Flex direction="row" alignItems="center" gap="size-50">
        <Text weight="heavy" size="S">
          {title}
        </Text>
        {help}
      </Flex>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart
          data={data}
          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--global-color-gray-500)"
            strokeOpacity={0.3}
          />
          <XAxis
            dataKey="ts"
            tickFormatter={(ts) =>
              new Date(ts).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })
            }
            tick={{ fontSize: 11, fill: "var(--global-text-color-700)" }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "var(--global-text-color-700)" }}
            width={40}
            tickFormatter={(v) =>
              typeof v === "number" ? `${Math.round(v)}${unit ?? ""}` : v
            }
          />
          <RechartsTooltip
            contentStyle={{
              background: "var(--global-background-color-default)",
              border: "1px solid var(--global-border-color-default)",
            }}
            labelFormatter={(ts) => new Date(ts).toLocaleString()}
            formatter={
              ((value: unknown) => [
                typeof value === "number" ? value.toFixed(2) : value,
                "",
              ]) as any
            }
          />
          {dataKeys.map((key, i) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={getChartColor(i, colors)}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function HistoryTrends({
  history,
}: {
  history: HealthHistoryResponse | null;
}) {
  if (!history || history.workers.length === 0) {
    return (
      <Text size="S" color="text-500">
        No history data available yet — samples accumulate every 5 seconds.
      </Text>
    );
  }

  const workerNames = history.workers.map((w) => w.name);

  const cpuData = transformHistoryForMetric(history.workers, "cpu_percent");
  const memData = transformHistoryForMetric(
    history.workers,
    "memory_percent"
  );
  const dbData = transformHistoryForMetric(
    history.workers,
    "db_pool_saturation"
  );
  const dbDataPct = dbData.map((pt) => {
    const newPt: Record<string, number | string | null> = { ts: pt.ts };
    for (const [k, v] of Object.entries(pt)) {
      if (k === "ts") continue;
      newPt[k] = typeof v === "number" ? v * 100 : v;
    }
    return newPt;
  });
  const lagData = transformHistoryForMetric(
    history.workers,
    "event_loop_lag_ms"
  );

  const hasCpu = hasAnyData(cpuData, workerNames);
  const hasMem = hasAnyData(memData, workerNames);
  const hasDb = hasAnyData(dbDataPct, workerNames);
  const hasLag = hasAnyData(lagData, workerNames);

  if (!hasCpu && !hasMem && !hasDb && !hasLag) {
    return (
      <Text size="S" color="text-500">
        History data exists but no chartable points yet — wait a few sampling
        intervals (5 seconds each) for the first points to appear.
      </Text>
    );
  }

  return (
    <Flex direction="column" gap="size-200">
      <Flex direction="row" gap="size-200" wrap>
        {hasCpu && (
          <div css={trendCardCSS}>
            <TrendChart
              title="CPU %"
              data={cpuData}
              dataKeys={workerNames}
              unit="%"
              help={
                <HelpTooltip label="CPU % (1-hour history)">
                  <RichTooltipDescription>
                    Per-process CPU usage as a percentage of the container's
                    configured CPU limit (`cpus` in docker-compose), sampled
                    every 5 seconds and kept for the last hour. One line per
                    registered worker.
                  </RichTooltipDescription>
                  <RichTooltipDescription>
                    Alarms: warning at 70% of the limit, critical at 90%.
                    Sustained high CPU starves the event loop, which is the
                    failure mode this page exists to catch.
                  </RichTooltipDescription>
                </HelpTooltip>
              }
            />
          </div>
        )}
        {hasMem && (
          <div css={trendCardCSS}>
            <TrendChart
              title="Memory %"
              data={memData}
              dataKeys={workerNames}
              unit="%"
              help={
                <HelpTooltip label="Memory % (1-hour history)">
                  <RichTooltipDescription>
                    Resident set size (RSS) of each worker as a percentage of
                    its container memory limit (`mem_limit` in
                    docker-compose), sampled every 5 seconds and kept for the
                    last hour.
                  </RichTooltipDescription>
                  <RichTooltipDescription>
                    Alarms: warning at 75% of the limit, critical at 90%. A
                    steadily climbing line indicates a leak; a sawtooth is
                    normal GC.
                  </RichTooltipDescription>
                </HelpTooltip>
              }
            />
          </div>
        )}
      </Flex>
      <Flex direction="row" gap="size-200" wrap>
        {hasDb && (
          <div css={trendCardCSS}>
            <TrendChart
              title="DB Pool Saturation %"
              data={dbDataPct}
              dataKeys={workerNames}
              unit="%"
              help={
                <HelpTooltip label="DB Pool Saturation % (1-hour history)">
                  <RichTooltipDescription>
                    Share of the SQLAlchemy async connection pool currently
                    checked out (checked-out connections divided by
                    `max_connections`), sampled every 5 seconds for the last
                    hour.
                  </RichTooltipDescription>
                  <RichTooltipDescription>
                    Alarms: warning at 80% checked out, critical at 95%.
                    Saturation at 100% means queries queue up waiting for a
                    connection even when CPU looks idle.
                  </RichTooltipDescription>
                </HelpTooltip>
              }
            />
          </div>
        )}
        {hasLag && (
          <div css={trendCardCSS}>
            <TrendChart
              title="Event-loop lag"
              data={lagData}
              dataKeys={workerNames}
              unit="ms"
              help={
                <HelpTooltip label="Event-loop lag (1-hour history)">
                  <RichTooltipDescription>
                    How long a zero-delay callback scheduled on the asyncio
                    event loop actually takes to run, in milliseconds. Sampled
                    every 5 seconds for the last hour; it is the most direct
                    measure of whether the process is responsive.
                  </RichTooltipDescription>
                  <RichTooltipDescription>
                    Alarms: warning at 100 ms, critical at 500 ms. This is the
                    2026-09-10 outage metric — lag spikes here before the loop
                    freezes, which is why it is tracked at all.
                  </RichTooltipDescription>
                </HelpTooltip>
              }
            />
          </div>
        )}
      </Flex>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Host & Allocation Panel
// ---------------------------------------------------------------------------

const allocationBarCSS = (pct: number, color: string) => css`
  width: 100%;
  height: 10px;
  background: rgba(var(--global-color-gray-500-rgb), 0.14);
  border-radius: 999px;
  overflow: hidden;
  position: relative;

  &::after {
    content: "";
    display: block;
    width: ${Math.min(Math.max(pct, 0), 100)}%;
    height: 100%;
    border-radius: 999px;
    background: ${color};
    transition: width 0.3s ease;
  }
`;

function AllocationBar({
  label,
  used,
  limit,
  unit,
  decimals = 1,
}: {
  label: string;
  used: number;
  limit: number;
  unit: string;
  decimals?: number;
}) {
  const pct = limit > 0 ? (used / limit) * 100 : 0;
  const color =
    pct >= 90
      ? "var(--global-color-danger)"
      : pct >= 75
        ? "var(--global-color-warning)"
        : "var(--global-color-success)";
  return (
    <Flex direction="column" gap="size-50">
      <Flex direction="row" justifyContent="space-between">
        <Text size="S">{label}</Text>
        <Text size="S" color="text-500">
          {used.toFixed(decimals)} / {limit.toFixed(decimals)}
          {unit ? ` ${unit}` : ""} ({pct.toFixed(1)}%)
        </Text>
      </Flex>
      <div css={allocationBarCSS(pct, color)} />
    </Flex>
  );
}

function formatBytes(bytes: number): string {
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DatabasePanel({ database }: { database?: DatabaseHealth | null }) {
  if (!database) {
    return (
      <Text size="S" color="text-500">
        Database health unavailable.
      </Text>
    );
  }

  const severity = database.reachable
    ? getAlarmSeverity(database.alarm_state)
    : "critical";

  return (
    <Flex direction="column" gap="size-150">
      <Flex direction="row" alignItems="center" gap="size-50">
        <span css={statusDotCSS(statusColor(severity))} />
        <Text weight="heavy" size="S">
          {database.dialect}
        </Text>
        <Badge
          variant={
            severity === "critical"
              ? "danger"
              : severity === "warn"
                ? "warning"
                : "success"
          }
        >
          {database.reachable ? "reachable" : "unreachable"}
        </Badge>
        <HelpTooltip label="Database">
          <RichTooltipDescription>
            Reachability, round-trip latency, connection count, and on-disk size
            of the database that backs Phoenix and the eval pipeline. Probed at
            most every 10 seconds behind a strict 2-second timeout, so a hung
            database shows unreachable instead of hanging this page.
          </RichTooltipDescription>
          <RichTooltipDescription>
            Connections above 70% of `max_connections` warn, above 90% are
            critical; probe latency above 250 ms warns, above 1000 ms is
            critical. Connection count, size, and idle-in-transaction are
            Postgres-only — SQLite reports size and latency only. No credentials
            or connection string are exposed here.
          </RichTooltipDescription>
        </HelpTooltip>
      </Flex>

      {!database.reachable && (
        <Text size="S" color="danger">
          {database.error ?? "Database probe failed."}
        </Text>
      )}

      {database.reachable && (
        <Flex direction="column" gap="size-100">
          {database.latency_ms != null && (
            <Flex direction="row" justifyContent="space-between">
              <Text size="S">Round-trip latency</Text>
              <Text size="S" color="text-500">
                {database.latency_ms.toFixed(1)} ms
              </Text>
            </Flex>
          )}
          {database.connections != null && database.max_connections != null && (
            <AllocationBar
              label="Connections"
              used={database.connections}
              limit={database.max_connections}
              unit=""
              decimals={0}
            />
          )}
          {database.idle_in_transaction != null &&
            database.idle_in_transaction > 0 && (
              <Flex direction="row" justifyContent="space-between">
                <Text size="S">Idle in transaction</Text>
                <Text size="S" color="text-500">
                  {database.idle_in_transaction} (&gt; 60s)
                </Text>
              </Flex>
            )}
          {database.size_bytes != null && (
            <Flex direction="row" justifyContent="space-between">
              <Text size="S">On-disk size</Text>
              <Text size="S" color="text-500">
                {formatBytes(database.size_bytes)}
              </Text>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
}

function HostAndAllocationPanel({
  workers,
  hostAlarmState,
}: {
  workers: WorkerHealth[];
  hostAlarmState: Record<string, AlarmState> | null;
}) {
  if (workers.length === 0) {
    return <Text color="text-500">No workers registered.</Text>;
  }

  const hostSeverity = getAlarmSeverity(hostAlarmState);

  // Host metrics describe the single physical machine shared by every
  // worker, so they are shown once instead of repeated on each worker card.
  const host = workers.find((worker) => {
    const latest = worker.latest;
    return (
      latest != null &&
      (latest.host_ram_total_mb != null ||
        latest.host_cpu_cores != null ||
        latest.host_source != null)
    );
  })?.latest;

  return (
    <Flex direction="column" gap="size-300">
      {/* Host — one shared physical machine, shown once */}
      <Flex direction="column" gap="size-150">
        <Flex direction="row" alignItems="center" gap="size-50">
          <Text weight="heavy" size="S" color="text-500">
            Host (shared physical machine)
          </Text>
          {hostSeverity !== "ok" && (
            <Badge
              variant={hostSeverity === "critical" ? "danger" : "warning"}
            >
              {hostSeverity === "critical" ? "critical" : "warning"}
            </Badge>
          )}
          <HelpTooltip label="Host (shared physical machine)">
            <RichTooltipDescription>
              Whole-machine metrics for the single physical host that runs
              every Evalight container. Unlike the per-container allocation
              table below, these numbers are shared — another tenant (trino,
              datahub, superset, …) can consume them independently of Evalight.
            </RichTooltipDescription>
            <RichTooltipDescription>
              RAM is total minus available; Swap is used out of total; Load avg
              is the 1-minute run-queue length against the host CPU core count.
              Thresholds: RAM available &lt; 15% warns, &lt; 5% is critical;
              swap &gt; 50% warns, &gt; 90% is critical; load &gt; 0.8× cores
              warns, &gt; 1.2× cores is critical.
            </RichTooltipDescription>
            <RichTooltipDescription>
              Source is opt-in via `HEALTH_NODE_EXPORTER_URL` (preferred) or a
              read-only host `/proc` mount. When neither is configured this
              block reads "unavailable" and only the allocation table renders.
            </RichTooltipDescription>
          </HelpTooltip>
        </Flex>
        {host ? (
          <Flex direction="column" gap="size-150">
            {host.host_ram_total_mb != null &&
              host.host_ram_available_mb != null && (
                <AllocationBar
                  label="RAM"
                  used={host.host_ram_total_mb - host.host_ram_available_mb}
                  limit={host.host_ram_total_mb}
                  unit="MB"
                />
              )}
            {host.host_swap_total_mb != null &&
              host.host_swap_used_mb != null && (
                <AllocationBar
                  label="Swap"
                  used={host.host_swap_used_mb}
                  limit={host.host_swap_total_mb}
                  unit="MB"
                />
              )}
            {host.host_load_avg_1m != null && host.host_cpu_cores != null && (
              <Flex direction="row" justifyContent="space-between">
                <Text size="S">Load avg</Text>
                <Text size="S" color="text-500">
                  {host.host_load_avg_1m.toFixed(2)} / {host.host_cpu_cores}{" "}
                  cores (
                  {((host.host_load_avg_1m / host.host_cpu_cores) * 100).toFixed(
                    0
                  )}
                  %)
                </Text>
              </Flex>
            )}
            {host.host_source && (
              <Text size="XS" color="text-500">
                Source: {host.host_source}
              </Text>
            )}
          </Flex>
        ) : (
          <Text size="S" color="text-500">
            Host metrics unavailable — set HEALTH_NODE_EXPORTER_URL or
            HEALTH_HOST_PROC_PATH.
          </Text>
        )}
      </Flex>

      {/* Evalight Allocation — one shared heading, one row per container */}
      <Flex direction="column" gap="size-150">
        <Flex direction="row" alignItems="center" gap="size-50">
          <Text weight="heavy" size="S" color="text-500">
            Evalight Allocation (per container)
          </Text>
          <HelpTooltip label="Evalight Allocation (per container)">
            <RichTooltipDescription>
              One row per Evalight process/container, read from that
              container's own cgroup v2 files. This is Evalight's own
              footprint, independent of whatever else is running on the shared
              host.
            </RichTooltipDescription>
            <RichTooltipDescription>
              Memory is current usage over the cgroup `memory.max` ceiling
              (the compose `mem_limit`); CPU limit is the cgroup `cpu.max`
              quota expressed in cores (the compose `cpus`); Memory usage is
              the same figure as a percentage. "No cgroup limits detected."
              means the container runs without a memory/CPU cap, so usage is
              unbounded by Evalight.
            </RichTooltipDescription>
            <RichTooltipDescription>
              Keep the combined footprint within the §7b host reservation
              (roughly 18–20 GB). A row above 90% of its ceiling is a
              container-level problem; the host block above tells you whether
              the machine itself has room.
            </RichTooltipDescription>
          </HelpTooltip>
        </Flex>
        <table css={tableCSS}>
          <thead>
            <tr>
              <Th label="Container" />
              <Th
                label="Memory"
                help={
                  <HelpTooltip label="Memory">
                    <RichTooltipDescription>
                      Current cgroup memory usage over the cgroup memory ceiling
                      (`memory.current` / `memory.max`), which mirrors the
                      compose `mem_limit`.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="CPU limit"
                help={
                  <HelpTooltip label="CPU limit">
                    <RichTooltipDescription>
                      The cgroup CPU quota (`cpu.max`) expressed in cores —
                      Evalight's share of the host CPU, set by the compose
                      `cpus` value.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Memory usage"
                help={
                  <HelpTooltip label="Memory usage">
                    <RichTooltipDescription>
                      Same memory figure as a percentage of the container's
                      ceiling. Green below 75%, amber from 75%, red at 90% and
                      above.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
            </tr>
          </thead>
          <tbody>
            {workers.map((worker) => {
              const latest = worker.latest;
              if (!latest) return null;
              const hasCgroup =
                latest.cgroup_memory_limit_bytes != null ||
                latest.cgroup_cpu_quota_cores != null;
              return (
                <tr key={worker.instance_id}>
                  <td>
                    <Text weight="heavy">{worker.name}</Text>
                    <br />
                    <Text size="S" color="text-500">
                      {worker.instance_id}
                    </Text>
                  </td>
                  <td>
                    {latest.cgroup_memory_limit_bytes != null &&
                    latest.cgroup_memory_usage_bytes != null
                      ? `${(latest.cgroup_memory_usage_bytes / (1024 * 1024)).toFixed(1)} / ${(latest.cgroup_memory_limit_bytes / (1024 * 1024)).toFixed(1)} MB`
                      : hasCgroup
                        ? "—"
                        : "No cgroup limits detected."}
                  </td>
                  <td>
                    {latest.cgroup_cpu_quota_cores != null
                      ? `${latest.cgroup_cpu_quota_cores.toFixed(2)} cores`
                      : "—"}
                  </td>
                  <td>
                    {latest.cgroup_memory_usage_pct != null
                      ? `${latest.cgroup_memory_usage_pct.toFixed(1)}%`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Flex>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Activity Feed Panel (Item 48)
// ---------------------------------------------------------------------------

const ACTIVITY_PAGE_SIZE = 10;

const activityPaginationCSS = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid var(--global-border-color-default);
`;

function formatActivityTime(ts: string): string {
  const d = new Date(ts);
  const now = new Date();
  const time = d.toLocaleTimeString();
  return d.toDateString() === now.toDateString()
    ? time
    : `${d.toLocaleDateString()} ${time}`;
}

function activityDetailText(ev: ActivityEvent): string {
  const parts: string[] = [];
  if (ev.detail && Object.keys(ev.detail).length > 0) {
    parts.push(JSON.stringify(ev.detail));
  }
  if (ev.trace_id) parts.push(`trace ${ev.trace_id}`);
  return parts.join(" • ");
}

// Windowed page numbers: [0, "…", 3, 4, 5, "…", 27]
function activityPageWindow(
  current: number,
  total: number,
  span = 2
): (number | "…")[] {
  const pages: (number | "…")[] = [];
  const start = Math.max(0, current - span);
  const end = Math.min(total - 1, current + span);
  if (start > 0) {
    pages.push(0);
    if (start > 1) pages.push("…");
  }
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) {
    if (end < total - 2) pages.push("…");
    pages.push(total - 1);
  }
  return pages;
}

function ActivityFeedPanel({ feed }: { feed: ActivityFeedResponse | null }) {
  const [page, setPage] = useState(0);
  const events = feed?.events ?? [];
  const totalPages = Math.max(1, Math.ceil(events.length / ACTIVITY_PAGE_SIZE));
  // Clamp for rendering; all navigation handlers use currentPage so the
  // stored value is reconciled on the next interaction (no setState effect).
  const currentPage = Math.min(page, totalPages - 1);

  if (!feed) {
    return <Loading />;
  }
  if (events.length === 0) {
    return (
      <Text size="S" color="text-500">
        No activity events yet.
      </Text>
    );
  }

  const startIndex = currentPage * ACTIVITY_PAGE_SIZE;
  const pageItems = events.slice(startIndex, startIndex + ACTIVITY_PAGE_SIZE);
  const newestTs = pageItems[0]?.ts;
  const oldestTs = pageItems[pageItems.length - 1]?.ts;

  return (
    <Flex direction="column" gap="size-100">
      <div css={tableScrollContainerCSS}>
        <table css={tableCSS}>
          <thead>
            <tr>
              <Th label="Time" />
              <Th
                label="Actor"
                help={
                  <HelpTooltip label="Actor">
                    <RichTooltipDescription>
                      Which daemon, template, or scan emitted the event (for
                      example `OnlineEvalDaemon`, a template name, or
                      `scan #91`). Its badge color reflects the event outcome.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Type"
                help={
                  <HelpTooltip label="Type">
                    <RichTooltipDescription>
                      The event category — tick, judge result, scan lifecycle,
                      batch flush, rate-limit backoff, budget threshold,
                      template toggle, worker restart, or config change.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Outcome"
                help={
                  <HelpTooltip label="Outcome">
                    <RichTooltipDescription>
                      ok, warn, error, or aborted. Error and critical render
                      red; warn renders amber. Hover a row for the full detail
                      payload and the related Phoenix trace id.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
            </tr>
          </thead>
          <tbody>
            {pageItems.map((ev) => (
              <tr key={ev.seq} title={activityDetailText(ev)}>
                <td>
                  <Text size="S">{formatActivityTime(ev.ts)}</Text>
                </td>
                <td>
                  <Badge
                    variant={
                      ev.outcome === "error"
                        ? "danger"
                        : ev.outcome === "warn"
                          ? "warning"
                          : ev.outcome === "critical"
                            ? "danger"
                            : "success"
                    }
                  >
                    {ev.actor}
                  </Badge>
                </td>
                <td>
                  <Text size="S" weight="heavy">
                    {ev.type}
                  </Text>
                </td>
                <td>
                  <Text size="S" color="text-500">
                    {ev.outcome}
                  </Text>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div css={activityPaginationCSS}>
        <Text size="S" color="text-500">
          {newestTs && oldestTs
            ? `${formatActivityTime(newestTs)} → ${formatActivityTime(oldestTs)} · `
            : ""}
          {`${startIndex + 1}-${Math.min(startIndex + ACTIVITY_PAGE_SIZE, events.length)} of ${events.length}`}
        </Text>
        <Flex direction="row" gap="size-50" alignItems="center">
          <Button
            size="S"
            variant="default"
            onPress={() => setPage(0)}
            isDisabled={currentPage === 0}
            aria-label="First page"
          >
            «
          </Button>
          <Button
            size="S"
            variant="default"
            onPress={() => setPage(Math.max(0, currentPage - 1))}
            isDisabled={currentPage === 0}
            aria-label="Previous page"
          >
            ‹
          </Button>
          {activityPageWindow(currentPage, totalPages).map((p, index) =>
            p === "…" ? (
              <Text key={`ellipsis-${index}`} size="S" color="text-500">
                …
              </Text>
            ) : (
              <Button
                key={p}
                size="S"
                variant={p === currentPage ? "primary" : "default"}
                onPress={() => setPage(p)}
                aria-label={`Page ${p + 1}`}
              >
                {p + 1}
              </Button>
            )
          )}
          <Button
            size="S"
            variant="default"
            onPress={() => setPage(Math.min(totalPages - 1, currentPage + 1))}
            isDisabled={currentPage >= totalPages - 1}
            aria-label="Next page"
          >
            ›
          </Button>
          <Button
            size="S"
            variant="default"
            onPress={() => setPage(totalPages - 1)}
            isDisabled={currentPage >= totalPages - 1}
            aria-label="Last page"
          >
            »
          </Button>
        </Flex>
      </div>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Trend Analysis Panel (Item 50)
// ---------------------------------------------------------------------------

function formatTrendValue(value: number | null): string {
  if (value == null) return "—";
  if (Math.abs(value) >= 1000) return value.toLocaleString();
  if (Math.abs(value) >= 10) return value.toFixed(1);
  return value.toFixed(2);
}

function TrendAnalysisPanel({ trends }: { trends: TrendsResponse | null }) {
  if (!trends) {
    return <Loading />;
  }
  const entries = Object.entries(trends.kpis);
  if (entries.length === 0) {
    return (
      <Text size="S" color="text-500">
        No trend samples recorded yet — baselines accumulate one sample per KPI
        per hour.
      </Text>
    );
  }
  return (
    <Flex direction="column" gap="size-100">
      {entries.map(([kpi, data]) => {
        const collecting = data.samples < 2;
        return (
          <Flex
            key={kpi}
            direction="row"
            gap="size-200"
            alignItems="center"
            justifyContent="space-between"
            css={css`
              padding: 10px 12px;
              border-radius: var(--global-rounding-medium, 8px);
              border: 1px solid var(--global-border-color-default);
              background: var(--global-background-color-default);
            `}
          >
            <Flex direction="row" gap="size-150" alignItems="center">
              <span
                css={statusDotCSS(
                  data.anomalous
                    ? data.severity === "critical"
                      ? "var(--global-color-danger, #ba1a1a)"
                      : "var(--global-color-warning, #ca8a04)"
                    : "var(--global-color-success, #16a34a)"
                )}
              />
              <Flex direction="column" gap="size-50">
                <Flex direction="row" alignItems="center">
                  <Text weight="heavy" size="S">
                    {metricDisplayName(kpi)}
                  </Text>
                  {trendMetricTooltip(kpi)}
                </Flex>
                <Text size="XS" color="text-500">
                  now {formatTrendValue(data.current)}
                  {data.baseline != null
                    ? ` · baseline ${formatTrendValue(data.baseline)}`
                    : ""}
                  {data.window === "hour_of_day"
                    ? " · vs same hour"
                    : data.window === "recent"
                      ? " · vs recent"
                      : ""}
                </Text>
              </Flex>
            </Flex>
            <Flex direction="row" gap="size-200" alignItems="center">
              {data.delta_pct != null && (
                <Text
                  size="S"
                  color={data.delta_pct >= 0 ? "success" : "danger"}
                >
                  {data.delta_pct >= 0 ? "+" : ""}
                  {data.delta_pct.toFixed(1)}%
                </Text>
              )}
              {data.anomalous ? (
                <Badge
                  variant={data.severity === "critical" ? "danger" : "warning"}
                >
                  {data.severity === "critical" ? "Critical" : "Anomaly"}
                </Badge>
              ) : collecting ? (
                <Badge variant="default">
                  {`Collecting baseline ${data.samples}/2`}
                </Badge>
              ) : null}
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Paginated Table Wrapper (Fix for unpaginated tables)
// ---------------------------------------------------------------------------

const tablePaginationCSS = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid var(--global-border-color-default);
`;

const tableScrollContainerCSS = css`
  max-height: 420px;
  overflow: auto;
  border: 1px solid var(--global-border-color-default);
  border-radius: var(--global-rounding-medium, 8px);

  & thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--global-background-color-default);
  }
`;

function usePagination<T>(items: T[], pageSize = 25) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  useEffect(() => {
    setPage(0);
  }, [items.length]);
  const start = page * pageSize;
  const end = start + pageSize;
  const pageItems = items.slice(start, end);
  return {
    page,
    pageItems,
    pageSize,
    totalPages,
    totalItems: items.length,
    setPage,
    startIndex: start,
    endIndex: Math.min(end, items.length),
  };
}

function PaginationControls({
  page,
  totalPages,
  startIndex,
  endIndex,
  totalItems,
  setPage,
}: {
  page: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
  totalItems: number;
  setPage: (p: number) => void;
}) {
  if (totalItems <= 0) return null;
  return (
    <div css={tablePaginationCSS}>
      <Text size="S" color="text-500">
        {totalItems > 0 ? `${startIndex + 1}-${endIndex} of ${totalItems}` : "0 items"}
      </Text>
      <Button
        variant="default"
        size="S"
        onPress={() => setPage(Math.max(0, page - 1))}
        isDisabled={page === 0}
        aria-label="Previous Page"
        leadingVisual={<Icon svg={<Icons.ArrowIosBackOutline />} />}
      />
      <Button
        size="S"
        onPress={() => setPage(Math.min(totalPages - 1, page + 1))}
        isDisabled={page >= totalPages - 1}
        aria-label="Next Page"
        leadingVisual={<Icon svg={<Icons.ArrowIosForwardOutline />} />}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template Project Panel (Item 49)
// ---------------------------------------------------------------------------

function TemplateProjectPanel({
  data,
  showInactive,
  onShowInactiveChange,
}: {
  data: TemplateProjectTableResponse | null;
  showInactive: boolean;
  onShowInactiveChange: (show: boolean) => void;
}) {
  const pagination = usePagination(data?.rows ?? [], 25);
  if (!data) {
    return <Loading />;
  }
  return (
    <>
      <Flex
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="size-200"
        wrap
      >
        <Text size="S" color="text-500">
          {data.enabled_count.toLocaleString()} enabled ·{" "}
          {data.inactive_count.toLocaleString()} inactive
        </Text>
        <Checkbox
          isSelected={showInactive}
          onChange={onShowInactiveChange}
          isDisabled={data.inactive_count === 0}
        >
          <Text size="S">Show inactive</Text>
        </Checkbox>
      </Flex>
      {data.rows.length === 0 ? (
        <Text size="S" color="text-500">
          {showInactive || data.inactive_count === 0
            ? "No template/project pairs recorded yet."
            : `No enabled template/project pairs — toggle “Show inactive” to see ${data.inactive_count.toLocaleString()} stopped pair(s).`}
        </Text>
      ) : (
        <>
          <div css={tableScrollContainerCSS}>
        <table css={tableCSS}>
          <thead>
            <tr>
              <Th label="Template" />
              <Th label="Project" />
              {showInactive ? (
                <Th
                  label="State"
                  help={
                    <HelpTooltip label="State">
                      <RichTooltipDescription>
                        Whether this (template, project) pair is currently
                        enabled for live evaluation. Disabled pairs keep their
                        historical metrics but run nothing.
                      </RichTooltipDescription>
                    </HelpTooltip>
                  }
                />
              ) : null}
              <Th
                label="Calls"
                help={
                  <HelpTooltip label="Calls">
                    <RichTooltipDescription>
                      Total judge calls this (template, project) pair made in
                      the current window.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Errors"
                help={
                  <HelpTooltip label="Errors">
                    <RichTooltipDescription>
                      Judge calls that failed (provider error, timeout,
                      validation, rate limit) for this pair.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Error Rate"
                help={
                  <HelpTooltip label="Error Rate">
                    <RichTooltipDescription>
                      Errors divided by calls. Green below 5%, amber at 5–10%,
                      red at 10% and above. A single red row here while every
                      aggregate KPI stays green is exactly what this table
                      exists to surface.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Avg Latency"
                help={
                  <HelpTooltip label="Avg Latency">
                    <RichTooltipDescription>
                      Mean judge-call latency for this pair in milliseconds,
                      including provider time.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Annotations"
                help={
                  <HelpTooltip label="Annotations">
                    <RichTooltipDescription>
                      Annotations written back to Phoenix (scores/labels
                      attached to spans) by this pair.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Budget Skips"
                help={
                  <HelpTooltip label="Budget Skips">
                    <RichTooltipDescription>
                      Judge calls that were intentionally skipped because the
                      pair's rate limit or daily USD budget was exhausted. A
                      non-zero value means evaluation was throttled, not that
                      it broke.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
            </tr>
          </thead>
          <tbody>
            {pagination.pageItems.map((row) => {
              const errorSeverity: "ok" | "warn" | "critical" =
                row.error_rate_pct >= 10
                  ? "critical"
                  : row.error_rate_pct >= 5
                    ? "warn"
                    : "ok";
              return (
                <tr key={`${row.template_id}-${row.project_name}`}>
                  <td>
                    <Text weight="heavy">{row.template_id}</Text>
                  </td>
                  <td>{row.project_name}</td>
                  {showInactive ? (
                    <td>
                      <Badge variant={row.enabled ? "success" : "default"}>
                        {row.enabled ? "enabled" : "stopped"}
                      </Badge>
                    </td>
                  ) : null}
                  <td>{row.judge_calls.toLocaleString()}</td>
                  <td>{row.judge_errors.toLocaleString()}</td>
                  <td>
                    <Flex direction="row" alignItems="center" gap="size-100">
                      <span
                        css={statusDotCSS(
                          errorSeverity === "critical"
                            ? "var(--global-color-danger, #ba1a1a)"
                            : errorSeverity === "warn"
                              ? "var(--global-color-warning, #ca8a04)"
                              : "var(--global-color-success, #16a34a)"
                        )}
                      />
                      <Text size="S">{row.error_rate_pct.toFixed(1)}%</Text>
                    </Flex>
                  </td>
                  <td>{row.avg_latency_ms.toFixed(0)}ms</td>
                  <td>{row.annotations_written.toLocaleString()}</td>
                  <td>{row.budget_skips.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
          </div>
          <PaginationControls {...pagination} />
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Freshness Panel (Item 51)
// ---------------------------------------------------------------------------

function FreshnessPanel({ data }: { data: FreshnessResponse | null }) {
  if (!data) {
    return <Loading />;
  }
  const summary = data.summary;
  const hasRows = data.rows.length > 0;

  const freshnessSeverity: "ok" | "warn" | "critical" =
    summary.overall_freshness_pct >= 95
      ? "ok"
      : summary.overall_freshness_pct >= 80
        ? "warn"
        : "critical";

  return (
    <Flex direction="column" gap="size-200">
      <Flex direction="row" gap="size-200" wrap>
        <KPICard
          title="Freshness"
          value={`${summary.overall_freshness_pct.toFixed(1)}%`}
          severity={freshnessSeverity}
          help={
            <HelpTooltip label="Freshness">
              <RichTooltipDescription>
                Fraction of eligible spans (over the last 24 hours, across all
                template/project pairs) that were judged within the SLO target
                of {summary.target_s}s (10 minutes) of ingest. The most direct
                answer to "are new traces being evaluated promptly?"
              </RichTooltipDescription>
              <RichTooltipDescription>
                Green at ≥95%, amber at ≥80%, red below 80%. A red freshness
                number with a healthy-looking daemon usually means the
                watermark cursor is behind, not that the process is down.
              </RichTooltipDescription>
            </HelpTooltip>
          }
        />
        <KPICard
          title={`Error budget remaining (SLO ${summary.slo_target_pct}%)`}
          value={
            summary.budget_exhausted
              ? "Exhausted"
              : `${summary.budget_remaining_pct.toFixed(1)}%`
          }
          severity={
            summary.budget_exhausted || summary.budget_remaining_pct <= 5
              ? "critical"
              : summary.budget_remaining_pct <= 20
                ? "warn"
                : "ok"
          }
          help={
            <HelpTooltip label="Error budget remaining">
              <RichTooltipDescription>
                The share of the SLO error budget still unspent. With an SLO of{" "}
                {summary.slo_target_pct}% within {summary.target_s}s,{" "}
                {(100 - summary.slo_target_pct).toFixed(1)}% of spans are
                allowed to be late — that allowance is the budget, and this is
                how much of it is left.
              </RichTooltipDescription>
              <RichTooltipDescription>
                It falls as Freshness drops below the {summary.slo_target_pct}%
                SLO and reads "Exhausted" once Freshness is under it. Amber at
                ≤20%, red at ≤5% or exhausted.
              </RichTooltipDescription>
            </HelpTooltip>
          }
        />
        <KPICard
          title="Eligible Spans"
          value={summary.total_eligible.toLocaleString()}
          severity="ok"
          help={
            <HelpTooltip label="Eligible Spans">
              <RichTooltipDescription>
                Spans over the last 24 hours that matched at least one
                template's filter and were therefore candidates for judging.
                This is the denominator of the freshness percentage — spans
                that no template is interested in are excluded.
              </RichTooltipDescription>
            </HelpTooltip>
          }
        />
        <KPICard
          title="Fresh Spans"
          value={summary.total_fresh.toLocaleString()}
          severity="ok"
          help={
            <HelpTooltip label="Fresh Spans">
              <RichTooltipDescription>
                Subset of eligible spans that were judged within the{" "}
                {summary.target_s}s target. Freshness is simply fresh ÷
                eligible; if this is tiny while eligible is large, the backlog
                is real.
              </RichTooltipDescription>
            </HelpTooltip>
          }
        />
      </Flex>

      {hasRows ? (
        <FreshnessTable rows={data.rows} sloPct={summary.slo_target_pct} />
      ) : (
        <Text size="S" color="text-500">
          No freshness data yet — spans must be ingested and judged before
          metrics appear.
        </Text>
      )}
    </Flex>
  );
}

function FreshnessTable({ rows, sloPct }: { rows: FreshnessRow[]; sloPct: number }) {
  const pagination = usePagination(rows, 25);
  return (
    <>
      <div css={tableScrollContainerCSS}>
        <table css={tableCSS}>
          <thead>
            <tr>
              <Th label="Template" />
              <Th label="Project" />
              <Th
                label="Freshness"
                help={
                  <HelpTooltip label="Freshness">
                    <RichTooltipDescription>
                      Percentage of this pair's eligible spans (last 24h) judged
                      within the target latency. Green ≥95%, amber ≥80%, red
                      below 80%.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Budget Rem."
                help={
                  <HelpTooltip label="Budget Rem.">
                    <RichTooltipDescription>
                      The share of this pair's SLO error budget still unspent.
                      It falls as the pair's Freshness drops below the {sloPct}%
                      SLO and reads "Exhausted" once Freshness is under it.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Eligible"
                help={
                  <HelpTooltip label="Eligible">
                    <RichTooltipDescription>
                      Spans for this pair that matched the template filter and
                      could therefore be judged.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Fresh"
                help={
                  <HelpTooltip label="Fresh">
                    <RichTooltipDescription>
                      Eligible spans that were judged within the target
                      latency.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
              <Th
                label="Oldest Unjudged"
                help={
                  <HelpTooltip label="Oldest Unjudged">
                    <RichTooltipDescription>
                      Age of the oldest eligible span still waiting to be
                      judged for this pair. A value larger than the target (600
                      s) is the concrete backlog driving the freshness number
                      down.
                    </RichTooltipDescription>
                  </HelpTooltip>
                }
              />
            </tr>
          </thead>
          <tbody>
            {pagination.pageItems.map((row) => {
              const rowSeverity: "ok" | "warn" | "critical" =
                row.freshness_pct >= 95
                  ? "ok"
                  : row.freshness_pct >= 80
                    ? "warn"
                    : "critical";
              return (
                <tr key={`${row.template_id}-${row.project_name}`}>
                  <td>
                    <Text weight="heavy">{row.template_id}</Text>
                  </td>
                  <td>{row.project_name}</td>
                  <td>
                    <Flex direction="row" alignItems="center" gap="size-100">
                      <span
                        css={statusDotCSS(
                          rowSeverity === "critical"
                            ? "var(--global-color-danger, #ba1a1a)"
                            : rowSeverity === "warn"
                              ? "var(--global-color-warning, #ca8a04)"
                              : "var(--global-color-success, #16a34a)"
                        )}
                      />
                      <Text size="S">{row.freshness_pct.toFixed(1)}%</Text>
                    </Flex>
                  </td>
                  <td>
                    {row.budget_exhausted
                      ? "Exhausted"
                      : `${row.budget_remaining_pct.toFixed(1)}%`}
                  </td>
                  <td>{row.eligible_spans}</td>
                  <td>{row.fresh_spans}</td>
                  <td>
                    {row.oldest_unjudged_age_s != null
                      ? `${row.oldest_unjudged_age_s.toFixed(0)}s`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <PaginationControls {...pagination} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Process Switcher (Item 44 / §6.9)
// ---------------------------------------------------------------------------

const processSwitcherTabsCSS = css`
  height: auto;
  flex: none;
`;

function ProcessSwitcher({
  workers,
  selected,
  onSelect,
}: {
  workers: WorkerHealth[];
  selected: string | null;
  onSelect: (id: string | null) => void;
}) {
  return (
    <Tabs
      css={processSwitcherTabsCSS}
      selectedKey={selected ?? "all"}
      onSelectionChange={(key) => onSelect(key === "all" ? null : (key as string))}
    >
      <TabList>
        <Tab key="all">All Processes</Tab>
        {workers.map((w) => (
          <Tab key={w.instance_id}>{w.name}</Tab>
        ))}
      </TabList>
    </Tabs>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const pageCSS = css`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  box-sizing: border-box;

  & > .card > .card__body {
    padding: 16px;
  }
`;

export function SystemHealthPage() {
  const [health, setHealth] = useState<SystemHealthResponse | null>(null);
  const [thresholds, setThresholds] = useState<ThresholdsResponse | null>(null);
  const [activity, setActivity] = useState<ActivitySummaryResponse | null>(
    null
  );
  const [history, setHistory] = useState<HealthHistoryResponse | null>(null);
  const [activityFeed, setActivityFeed] = useState<ActivityFeedResponse | null>(
    null
  );
  const [trends, setTrends] = useState<TrendsResponse | null>(null);
  const [templates, setTemplates] = useState<TemplateProjectTableResponse | null>(
    null
  );
  const [freshness, setFreshness] = useState<FreshnessResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
  const [showInactiveTemplates, setShowInactiveTemplates] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const notifyError = useNotifyError();
  // Guards the 5s poll against overlapping requests and a toast on every
  // failed tick when the backend is down.
  const inFlightRef = useRef(false);
  const pollErrorNotifiedRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    async function fetchJson<T>(path: string): Promise<T | null> {
      try {
        const response = await authFetch(prependBasename(path), { signal });
        if (!response.ok) {
          throw new Error(`${path} failed: ${response.statusText}`);
        }
        return (await response.json()) as T;
      } catch (error) {
        if (signal.aborted) {
          return null;
        }
        // Non-critical endpoints are auxiliary; log and keep the page usable.
        console.warn(`Failed to load ${path}:`, error);
        return null;
      }
    }

    async function fetchHealth() {
      try {
        const response = await authFetch(prependBasename("/v1/system/health"), {
          signal,
        });
        if (!response.ok) {
          throw new Error(`Health check failed: ${response.statusText}`);
        }
        const data = (await response.json()) as SystemHealthResponse;
        if (signal.aborted) {
          return;
        }
        setHealth(data);
        pollErrorNotifiedRef.current = false;
      } catch (error) {
        if (signal.aborted) {
          return;
        }
        // Only surface a toast for the first failure in a streak — otherwise
        // a down backend would toast on every 5s tick.
        if (!pollErrorNotifiedRef.current) {
          pollErrorNotifiedRef.current = true;
          notifyError({
            title: "Failed to load system health",
            message: error instanceof Error ? error.message : "Unknown error",
          });
        }
      }
    }

    async function fetchAuxiliary() {
      const [
        thresholdsData,
        activityData,
        historyData,
        activityFeedData,
        trendsData,
        templatesData,
        freshnessData,
      ] = await Promise.all([
        fetchJson<ThresholdsResponse>("/v1/system/health/thresholds"),
        fetchJson<ActivitySummaryResponse>(
          "/v1/system/activity-summary?minutes=60"
        ),
        fetchJson<HealthHistoryResponse>("/v1/system/health/history?minutes=60"),
        fetchJson<ActivityFeedResponse>("/v1/system/activity?limit=500"),
        fetchJson<TrendsResponse>("/v1/system/trends"),
        fetchJson<TemplateProjectTableResponse>(
          `/v1/system/templates?include_inactive=${showInactiveTemplates}`
        ),
        fetchJson<FreshnessResponse>("/v1/system/freshness"),
      ]);
      if (signal.aborted) {
        return;
      }
      if (thresholdsData) {
        setThresholds(thresholdsData);
      }
      if (activityData) {
        setActivity(activityData);
      }
      if (historyData) {
        setHistory(historyData);
      }
      if (activityFeedData) {
        setActivityFeed(activityFeedData);
      }
      if (trendsData) {
        setTrends(trendsData);
      }
      if (templatesData) {
        setTemplates(templatesData);
      }
      if (freshnessData) {
        setFreshness(freshnessData);
      }
    }

    async function loadAll(isInitial: boolean) {
      // Never stack requests: if the previous tick is still in flight, skip.
      if (inFlightRef.current) {
        return;
      }
      // Skip hidden tabs; the visibilitychange handler below refreshes as soon
      // as the tab is visible again, so we don't need to poll in the
      // background.
      if (!isInitial && document.visibilityState !== "visible") {
        return;
      }
      inFlightRef.current = true;
      try {
        await Promise.all([fetchHealth(), fetchAuxiliary()]);
      } finally {
        inFlightRef.current = false;
        if (isInitial && !signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadAll(true);

    const interval = window.setInterval(() => {
      loadAll(false);
    }, 5000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadAll(false);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [notifyError, refreshNonce, showInactiveTemplates]);

  if (loading && !health) {
    return (
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
      >
        <Loading />
      </Flex>
    );
  }

  const allWorkers = health?.workers ?? [];
  const hostAlarmState = health?.host_alarm_state ?? null;
  const workers = selectedWorkerId
    ? allWorkers.filter((w) => w.instance_id === selectedWorkerId)
    : allWorkers;

  // Headline KPIs are the *worst* worker, not an average: averaging hides one
  // hot process behind idle ones (the two processes are unlike — web server
  // vs eval worker). Each card also lists every worker's value so the headline
  // is attributable. History Trends still charts the mean, and says so,
  // because one line per metric reads better than N.
  const worstOf = (
    workers_: WorkerHealth[],
    pick: (w: WorkerHealth) => number | null | undefined,
    scale = 1
  ): { value: number | null; worker: WorkerHealth | null } => {
    const rows = workers_
      .map((w) => ({ worker: w, value: pick(w) }))
      .filter(
        (r): r is { worker: WorkerHealth; value: number } => r.value != null
      )
      .sort((a, b) => b.value - a.value);
    if (rows.length === 0) return { value: null, worker: null };
    return { value: rows[0].value * scale, worker: rows[0].worker };
  };

  const cpu = worstOf(workers, (w) => w.latest?.cpu_percent);
  const mem = worstOf(workers, (w) => w.latest?.memory_percent);
  const lag = worstOf(workers, (w) => w.latest?.event_loop_lag_ms);
  const dbSat = worstOf(workers, (w) => w.latest?.db_pool_saturation, 100);
  const budget = worstOf(workers, (w) => w.latest?.daily_budget_spent_pct);

  const cpuSeverity = metricSeverity(cpu.worker, "process_cpu_pct");
  const memSeverity = metricSeverity(mem.worker, "rss_pct");
  const lagSeverity = metricSeverity(lag.worker, "event_loop_lag_ms");
  const dbSeverity = metricSeverity(dbSat.worker, "db_pool_checked_out_pct");
  const budgetSeverity = metricSeverity(
    budget.worker,
    "daily_budget_spent_pct"
  );

  const cpuBreakdown = workerBreakdown(
    workers,
    (w) => w.latest?.cpu_percent,
    (v) => `${v.toFixed(1)}%`
  );
  const memBreakdown = workerBreakdown(
    workers,
    (w) => w.latest?.memory_percent,
    (v) => `${v.toFixed(1)}%`
  );
  const lagBreakdown = workerBreakdown(
    workers,
    (w) => w.latest?.event_loop_lag_ms,
    (v) => `${v.toFixed(0)}ms`
  );
  const dbBreakdown = workerBreakdown(
    workers,
    (w) => w.latest?.db_pool_saturation,
    (v) => `${(v * 100).toFixed(0)}%`
  );
  const budgetBreakdown = workerBreakdown(
    workers,
    (w) => w.latest?.daily_budget_spent_pct,
    (v) => `${v.toFixed(1)}%`
  );

  const filteredHistory: HealthHistoryResponse | null = history
    ? {
        ...history,
        workers: selectedWorkerId
          ? history.workers.filter((w) => w.instance_id === selectedWorkerId)
          : history.workers,
      }
    : null;

  const overallSeverity: Severity =
    collectAlarmsBySeverity(allWorkers, "critical").length > 0 ||
    collectHostAlarmsBySeverity(hostAlarmState, "critical").length > 0
      ? "critical"
      : collectAlarmsBySeverity(allWorkers, "warn").length > 0 ||
          collectHostAlarmsBySeverity(hostAlarmState, "warn").length > 0
        ? "warn"
        : "ok";

  const updatedAt = health?.ts
    ? new Date(health.ts).toLocaleTimeString()
    : null;

  return (
    <div css={pageCSS}>
      <Flex direction="column" gap="size-100">
        <Flex
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap="size-200"
          wrap
        >
          <Flex direction="row" alignItems="center" gap="size-100">
            <Heading level={1}>System Health</Heading>
            <Badge
              variant={
                overallSeverity === "critical"
                  ? "danger"
                  : overallSeverity === "warn"
                    ? "warning"
                    : "success"
              }
            >
              {overallSeverity === "critical"
                ? "Incident"
                : overallSeverity === "warn"
                  ? "Degraded"
                  : "Operational"}
            </Badge>
          </Flex>
          <Flex direction="row" alignItems="center" gap="size-100">
            <Text size="S" color="text-500">
              {health?.workers_count ?? 0} worker
              {(health?.workers_count ?? 0) === 1 ? "" : "s"} · every{" "}
              {health?.sample_interval_s ?? 5}s
            </Text>
            <Button
              size="S"
              variant="default"
              onPress={() => setRefreshNonce((n) => n + 1)}
              leadingVisual={<Icon svg={<Icons.Refresh />} />}
            >
              Refresh
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <StatusOverview
        workers={allWorkers}
        hostAlarmState={hostAlarmState}
        sampleIntervalS={health?.sample_interval_s ?? 5}
        updatedAt={updatedAt}
      />

      {allWorkers.length > 1 && (
        <ProcessSwitcher
          workers={allWorkers}
          selected={selectedWorkerId}
          onSelect={setSelectedWorkerId}
        />
      )}

      <AlarmBanners workers={allWorkers} hostAlarmState={hostAlarmState} />

      <Card
        title="KPIs"
        collapsible={false}
        titleExtra={
          <HelpTooltip label="KPIs">
            <RichTooltipDescription>
              Process-wide headline metrics, refreshed every{" "}
              {health?.sample_interval_s ?? 5}s from the latest sample of every
              registered worker. Each card shows the worst worker's value (not
              an average) and lists every worker underneath so the headline is
              attributable.
            </RichTooltipDescription>
            <RichTooltipDescription>
              Each card's dot and border reflect that metric's own alarm state
              on the worst worker: green = ok, amber = warning, red = critical.
              Use the process switcher above to scope History Trends and Host
              &amp; Allocation to a single worker.
            </RichTooltipDescription>
          </HelpTooltip>
        }
      >
        <Flex direction="row" gap="size-200" wrap>
          <KPICard
            title="CPU (worst worker)"
            value={cpu.value != null ? `${cpu.value.toFixed(1)}%` : "—"}
            severity={cpuSeverity}
            breakdown={cpuBreakdown}
            help={
              <HelpTooltip label="CPU">
                <RichTooltipDescription>
                  Highest CPU usage of any worker as a percentage of that
                  container's CPU limit (compose `cpus`). Warning at 70%,
                  critical at 90%. The process switcher scopes this to one
                  worker.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
          <KPICard
            title="Memory (worst worker)"
            value={mem.value != null ? `${mem.value.toFixed(1)}%` : "—"}
            severity={memSeverity}
            breakdown={memBreakdown}
            help={
              <HelpTooltip label="Memory">
                <RichTooltipDescription>
                  Highest resident memory of any worker as a percentage of that
                  container's memory limit (compose `mem_limit`). Warning at
                  75%, critical at 90%.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
          <KPICard
            title="Event-loop lag"
            value={lag.value != null ? `${lag.value.toFixed(0)}ms` : "—"}
            severity={lagSeverity}
            breakdown={lagBreakdown}
            help={
              <HelpTooltip label="Event-loop lag">
                <RichTooltipDescription>
                  Worst event-loop scheduling lag across workers: how late a
                  zero-delay asyncio callback actually ran. Warning at 100 ms,
                  critical at 500 ms — the 2026-09-10 failure mode.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
          <KPICard
            title="DB Pool"
            value={dbSat.value != null ? `${dbSat.value.toFixed(0)}%` : "—"}
            severity={dbSeverity}
            breakdown={dbBreakdown}
            help={
              <HelpTooltip label="DB Pool">
                <RichTooltipDescription>
                  Highest SQLAlchemy async connection-pool saturation across
                  workers (checked-out ÷ max connections). Warning at 80%,
                  critical at 95%; at 100% queries wait for a connection.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
          <KPICard
            title="Daily Budget"
            value={budget.value != null ? `${budget.value.toFixed(1)}%` : "—"}
            severity={budgetSeverity}
            breakdown={budgetBreakdown}
            help={
              <HelpTooltip label="Daily Budget">
                <RichTooltipDescription>
                  Highest per-worker share of the daily eval USD budget
                  consumed (that worker's spent ÷ its limit, across its
                  template budgets). Warning at 80%, critical at 100%, at which
                  point the daemon self-stops.
                </RichTooltipDescription>
              </HelpTooltip>
            }
          />
        </Flex>
      </Card>

      <Card
        title="History Trends"
        collapsible={false}
        titleExtra={
          <HelpTooltip label="History Trends">
            <RichTooltipDescription>
              One line per worker for the last hour, sampled every 5 seconds.
              Charts appear only once there is at least one chartable point in
              the ring buffer; use the process switcher above to isolate a
              single worker.
            </RichTooltipDescription>
          </HelpTooltip>
        }
      >
        <HistoryTrends history={filteredHistory} />
      </Card>

      <Card
        title="Host & Allocation"
        collapsible={false}
        titleExtra={
          <HelpTooltip label="Host & Allocation">
            <RichTooltipDescription>
              Separates the shared physical host (one set of RAM/swap/load
              numbers for the machine) from Evalight's own per-container
              cgroup allocation. This is how you tell "Evalight is
              misbehaving" apart from "the machine is full because of
              something else."
            </RichTooltipDescription>
          </HelpTooltip>
        }
      >
        <HostAndAllocationPanel workers={workers} hostAlarmState={hostAlarmState} />
      </Card>

      <Card
        title="Database"
        collapsible={false}
        titleExtra={
          <HelpTooltip label="Database">
            <RichTooltipDescription>
              Whether the database is reachable, and how hard it is working —
              the concurrency ceiling is the resource most easily exhausted by
              an eval backlog, and it was previously invisible on this page.
              Sampled behind a 2-second timeout and a 10-second cache so a slow
              database never adds load to itself.
            </RichTooltipDescription>
          </HelpTooltip>
        }
      >
        <DatabasePanel database={health?.database} />
      </Card>

      <Card
        title="Workers"
        collapsible={false}
        titleExtra={
          <HelpTooltip label="Workers">
            <RichTooltipDescription>
              One row per registered process (a "worker" is a DaemonTask:
              OnlineEvalDaemon/ExperimentRunner/…, registered by name). Status
              is the worst alarm state on that worker. Older rows from
              processes that have since restarted or been renamed are filtered
              out once their last sample is more than 25 seconds old.
            </RichTooltipDescription>
          </HelpTooltip>
        }
      >
        {workers.length === 0 ? (
          <Text color="text-500">No workers registered.</Text>
        ) : (
          <WorkersTable workers={workers} />
        )}
      </Card>

      <Card
        title="Activity Summary"
        collapsible={false}
        titleExtra={
          <HelpTooltip label="Activity Summary">
            <RichTooltipDescription>
              A 60-minute window over eval traffic: traces and spans ingested,
              experiments currently running, and red-team scans started. It
              answers "is there work happening at all?" before you dig into
              per-worker detail.
            </RichTooltipDescription>
          </HelpTooltip>
        }
      >
        <ActivitySummary activity={activity} />
      </Card>

      <Card
        title="Activity Feed"
        collapsible={false}
        titleExtra={
          <HelpTooltip label="Activity Feed">
            <RichTooltipDescription>
              A rolling, paged stream of discrete events — judge results, scan
              lifecycle, rate-limit backoff, budget thresholds, template
              enable/disable, worker restarts, config changes — newest first.
              Hover a row to see its structured detail and trace id.
            </RichTooltipDescription>
          </HelpTooltip>
        }
      >
        <ActivityFeedPanel feed={activityFeed} />
      </Card>

      <Card
        title="Trend Analysis"
        collapsible={false}
        titleExtra={
          <HelpTooltip label="Trend Analysis">
            <RichTooltipDescription>
              Compares each KPI's current value against a baseline built from
              the trailing 7 days at the same hour (falling back to the most
              recent samples when the same-hour window has fewer than two).
              Values are recorded once per hour.
            </RichTooltipDescription>
            <RichTooltipDescription>
              A dot turns amber/red when the current value deviates by more
              than 3 standard deviations from the baseline. Severity is
              critical when that deviation is also at least 50% of the
              baseline. "Collecting baseline n/2" means there are not yet two
              samples to compare. This catches problems a static threshold
              misses — "unusual", not necessarily "bad".
            </RichTooltipDescription>
          </HelpTooltip>
        }
      >
        <TrendAnalysisPanel trends={trends} />
      </Card>

      <Card
        title="Templates"
        collapsible={false}
        titleExtra={
          <HelpTooltip label="Templates">
            <RichTooltipDescription>
              One row per active (template, project) pair over the current
              window: total judge calls, errors, error rate, average latency,
              annotations written, and budget skips. This is the grain where a
              single failing template shows up even while the aggregate KPIs
              stay green. Error rate turns amber at 5% and red at 10%.
            </RichTooltipDescription>
          </HelpTooltip>
        }
      >
        <TemplateProjectPanel
          data={templates}
          showInactive={showInactiveTemplates}
          onShowInactiveChange={setShowInactiveTemplates}
        />
      </Card>

      <Card
        title="Freshness / SLO"
        collapsible={false}
        titleExtra={
          <HelpTooltip label="Freshness / SLO">
            <RichTooltipDescription>
              An SLO-style view of online evaluation timeliness: of the spans
              that were eligible to be judged in the last 24 hours, what
              fraction were actually judged within the target latency
              (`EVAL_FRESHNESS_TARGET_S`, default 600s)? The SLO
              (`EVAL_FRESHNESS_SLO_PCT`, default 95%) sizes the error budget,
              and the panel shows how much of it remains unspent.
            </RichTooltipDescription>
            <RichTooltipDescription>
              Freshness is green at ≥95%, amber at ≥80%, red below 80%. The
              per-pair table below shows which template/project is burning the
              budget and the age of its oldest un-judged span.
            </RichTooltipDescription>
          </HelpTooltip>
        }
      >
        <FreshnessPanel data={freshness} />
      </Card>

      {thresholds && (
        <Card
          title="Thresholds"
          collapsible
          defaultOpen={false}
          titleExtra={
            <HelpTooltip label="Thresholds">
              <RichTooltipDescription>
                The warn/critical boundaries the sampler evaluates every tick
                to produce each metric's alarm state. Change them at runtime
                via `GET/PUT /v1/system/health/thresholds`; the values shown
                here are what is currently in effect.
              </RichTooltipDescription>
            </HelpTooltip>
          }
        >
          <View>
            <table css={tableCSS}>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Warn</th>
                  <th>Critical</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(thresholds.thresholds).map(
                  ([metric, entry]) => (
                    <tr key={metric}>
                      <td>{metricDisplayName(metric)}</td>
                      <td>{entry.warn}</td>
                      <td>{entry.critical}</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </View>
        </Card>
      )}
    </div>
  );
}
